import { useLoaderData, useNavigate } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Alert } from '@ds/feedback';
import { Tabs } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { dados } from '@/data/porta';
import type { AbaDeConfiguracoes, CargaDeConfiguracoes, CargaDoPainelDeAnuncios, DocumentoDaBase, ModeloDeDocumento } from '@/data/tipos';
import type {
    AssinaturaVistaPelaClinica, CargaDeAtendimentoECrm, CategoriaDeProcedimento, PacoteDoCatalogo, PreferenciaSalva,
} from '@/data/cadastros';
import { rotaPorId } from '@/router/rotas';
import { pode } from '@/session/sessao';

/**
 * As abas cujo cadastro só quem administra altera (permissão `configuracoes`,
 * a mesma que a RLS da `0080` cobra). Quem não tem lê, e é avisado antes de
 * tentar — a recusa de verdade é do banco.
 */
const ABAS_DE_CADASTRO: AbaDeConfiguracoes[] = ['preferencias', 'atendimento-crm', 'categorias', 'pacotes', 'etiquetas'];
import { AssinaturaDaClinicaAba } from './configuracoes/AssinaturaDaClinicaAba';
import { AtendimentoECrm } from './configuracoes/AtendimentoECrm';
import { BaseDeConhecimento } from './configuracoes/BaseDeConhecimento';
import { CategoriasDeProcedimentoAba } from './configuracoes/CategoriasDeProcedimentoAba';
import { DadosDaClinicaAba } from './configuracoes/DadosDaClinicaAba';
import { EquipeDaClinica } from './configuracoes/EquipeDaClinica';
import { EtiquetasAba } from './configuracoes/EtiquetasAba';
import { IntegracoesDaClinica } from './configuracoes/IntegracoesDaClinica';
import { JornadaSemanal } from './configuracoes/JornadaSemanal';
import { ModelosDeAtestadoAba } from './configuracoes/ModelosDeAtestadoAba';
import { PacotesDoCatalogoAba } from './configuracoes/PacotesDoCatalogoAba';
import { PreferenciasDoSistema } from './configuracoes/PreferenciasDoSistema';
import type { OpcoesDasPreferencias } from './configuracoes/PreferenciasDoSistema';
import { ReguaDeLembretes } from './configuracoes/ReguaDeLembretes';
import { SalasDaClinica } from './configuracoes/SalasDaClinica';

const ABAS: AbaDeConfiguracoes[] = [
    'clinica', 'preferencias', 'equipe', 'agenda', 'notificacoes', 'atendimento-crm',
    'integracoes', 'assistente', 'assinatura', 'categorias', 'pacotes', 'modelos', 'etiquetas',
];

function abaDaRota(pathname: string): AbaDeConfiguracoes {
    const ultimo = pathname.split('/').pop() ?? '';
    return (ABAS as string[]).includes(ultimo) ? (ultimo as AbaDeConfiguracoes) : 'clinica';
}

/**
 * O que cada aba carrega além do cadastro comum.
 *
 * Só a da aba aberta: buscar preferências, filas e faturas a cada troca de aba
 * seria custo por clique para mostrar uma só.
 */
export type ExtrasDaAba =
    | { tipo: 'nenhum' }
    | { tipo: 'preferencias'; salvas: PreferenciaSalva[]; opcoes: OpcoesDasPreferencias }
    | { tipo: 'atendimento-crm'; carga: CargaDeAtendimentoECrm }
    | { tipo: 'integracoes'; carga: CargaDeAtendimentoECrm; anuncios: CargaDoPainelDeAnuncios | null }
    | { tipo: 'assinatura'; assinatura: AssinaturaVistaPelaClinica }
    | { tipo: 'categorias'; categorias: CategoriaDeProcedimento[] }
    | { tipo: 'pacotes'; pacotes: PacoteDoCatalogo[] }
    | { tipo: 'modelos'; modelos: ModeloDeDocumento[] };

export interface CargaDaTelaDeConfiguracoes extends CargaDeConfiguracoes {
    /** Só na aba do assistente: buscar a base nas outras seria custo por clique. */
    base: DocumentoDaBase[];
    extras: ExtrasDaAba;
}

async function extrasDa(aba: AbaDeConfiguracoes): Promise<ExtrasDaAba> {
    switch (aba) {
        case 'preferencias': {
            /* As opções de conta, método e categoria são o cadastro do
               financeiro: preferência que aponta para conta inventada aponta
               para nada. */
            const [salvas, financeiro] = await Promise.all([dados().preferencias(), dados().financeiro({ aba: 'contas' })]);
            return {
                tipo: 'preferencias',
                salvas,
                opcoes: {
                    contas: financeiro.contas.map((c) => ({ valor: c.id, rotulo: c.nome })),
                    metodos: financeiro.metodos.map((m) => ({ valor: m.id, rotulo: m.nome })),
                    categoriasReceita: financeiro.categorias.filter((c) => c.natureza === 'receita').map((c) => ({ valor: c.id, rotulo: c.nome })),
                    categoriasDespesa: financeiro.categorias.filter((c) => c.natureza === 'despesa').map((c) => ({ valor: c.id, rotulo: c.nome })),
                },
            };
        }
        case 'atendimento-crm':
        case 'etiquetas':
            return { tipo: 'atendimento-crm', carga: await dados().atendimentoECrm() };
        case 'integracoes': {
            /* A fila e a pessoa padrão do canal (0161) e a conta da Meta (0162). Sem
               permissão de marketing o cartão da Meta simplesmente não aparece. */
            const [carga, anuncios] = await Promise.all([
                dados().atendimentoECrm(),
                dados().painelDeAnuncios(1).catch(() => null),
            ]);
            return { tipo: 'integracoes', carga, anuncios };
        }
        case 'assinatura':
            return { tipo: 'assinatura', assinatura: await dados().assinaturaDaClinica() };
        case 'categorias':
            return { tipo: 'categorias', categorias: await dados().categoriasDeProcedimento() };
        case 'pacotes':
            return { tipo: 'pacotes', pacotes: await dados().pacotesDoCatalogo() };
        case 'modelos': {
            /* Atestado e prescrição são modelos de documento — a mesma lista
               da tela de Documentos, recortada pelas duas categorias. */
            const carga = await dados().documentos({ aba: 'modelos', porPagina: 500 });
            return { tipo: 'modelos', modelos: carga.modelos.filter((m) => m.categoria === 'Atestado' || m.categoria === 'Prescrição') };
        }
        default:
            return { tipo: 'nenhum' };
    }
}

export async function carregarConfiguracoes({ request }: LoaderFunctionArgs): Promise<CargaDaTelaDeConfiguracoes> {
    const aba = abaDaRota(new URL(request.url).pathname);
    const [carga, base, extras] = await Promise.all([
        dados().configuracoes(aba),
        aba === 'assistente' ? dados().baseDaClinica() : Promise.resolve([]),
        extrasDa(aba),
    ]);
    return { ...carga, base, extras };
}

export function Configuracoes() {
    const carga = useLoaderData() as CargaDaTelaDeConfiguracoes;
    const navigate = useNavigate();
    const rota = rotaPorId('configuracoes');
    const { aba, extras } = carga;

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Configurações"
                description="O cadastro que as outras telas leem: dados da clínica, jornada, salas e quem tem acesso."
            >
                <Tabs
                    ariaLabel="Seções das configurações" activeId={aba}
                    onChange={(id) => navigate(`/app/configuracoes/${id}`)}
                    tabs={(rota?.children ?? []).map((c) => ({ id: c.path, label: c.label }))}
                />
            </PageHeader>

            {ABAS_DE_CADASTRO.includes(aba) && !pode('configuracoes') && (
                <Alert tone="info" title="Você consulta estes cadastros; alterar é de quem administra a clínica">
                    Preferências, filas, etiquetas, categorias e pacotes mudam o trabalho de toda a equipe. Peça à proprietária,
                    ou a quem recebeu a permissão de configurações.
                </Alert>
            )}

            {aba === 'clinica' && <DadosDaClinicaAba carga={carga} />}

            {aba === 'preferencias' && extras.tipo === 'preferencias' && (
                <PreferenciasDoSistema salvas={extras.salvas} opcoes={extras.opcoes} />
            )}

            {aba === 'equipe' && <EquipeDaClinica equipe={carga.equipe} vagas={carga.vagas} />}

            {aba === 'agenda' && (
                <>
                    <JornadaSemanal horarios={carga.horarios} horasSemanais={carga.horasSemanais} />
                    <SalasDaClinica salas={carga.salas} />
                </>
            )}

            {aba === 'notificacoes' && (
                <ReguaDeLembretes
                    lembretes={carga.lembretes}
                    canalConectado={carga.canais.some((c) => c.status === 'conectado')}
                />
            )}

            {aba === 'atendimento-crm' && extras.tipo === 'atendimento-crm' && <AtendimentoECrm carga={extras.carga} />}

            {aba === 'integracoes' && (
                <IntegracoesDaClinica
                    canais={carga.canais} pareamentoDisponivel={carga.pareamentoDisponivel}
                    atendimento={extras.tipo === 'integracoes' ? extras.carga : undefined}
                    anuncios={extras.tipo === 'integracoes' ? extras.anuncios : null}
                />
            )}

            {aba === 'assistente' && <BaseDeConhecimento documentos={carga.base} />}

            {aba === 'assinatura' && extras.tipo === 'assinatura' && <AssinaturaDaClinicaAba assinatura={extras.assinatura} />}

            {aba === 'categorias' && extras.tipo === 'categorias' && <CategoriasDeProcedimentoAba categorias={extras.categorias} />}

            {aba === 'pacotes' && extras.tipo === 'pacotes' && <PacotesDoCatalogoAba pacotes={extras.pacotes} />}

            {aba === 'modelos' && extras.tipo === 'modelos' && <ModelosDeAtestadoAba modelos={extras.modelos} />}

            {aba === 'etiquetas' && extras.tipo === 'atendimento-crm' && <EtiquetasAba carga={extras.carga} />}
        </div>
    );
}
